import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, MonitorSmartphone, RefreshCw, X } from 'lucide-react';
import { unterschriftAPI } from '@/lib/api';

/* Unterschrift-Station (Bauauftrag 03.10.2026): Dialog am iMac nach „Zur Unterschrift senden".
   Fragt den Vorgang alle zwei Sekunden ab (gesendet -> wird angezeigt -> unterschrieben), zeigt den
   QR-Code als Ausweichweg fuers Handy des Kunden und bietet „Abbrechen" und „Erneut senden".
   Der Dialog ist modal: solange er offen ist, laesst sich der Entwurf nicht aendern. Schliessen
   bricht den Vorgang ab — ein geaenderter Entwurf muss neu gesendet werden. Waehrend der Vertrag
   angelegt wird, laesst sich der Dialog nicht schliessen (das Ergebnis muss am iMac ankommen). */

const ABFRAGE_MS = 2000;
const LAEUFT = ['offen', 'angezeigt', 'wird_abgeschlossen'];
const SCHRITTE = [
  ['offen', 'Gesendet'],
  ['angezeigt', 'Wird angezeigt'],
  ['unterschrieben', 'Unterschrieben'],
];

// Wortlaut je Art des Vorgangs (03.10.2026: auch Bestandsvertrag, neue Laufzeit und Zusatzvereinbarung)
const SACHE = {
  vertrag: { der: 'Der Vertrag', den: 'den Vertrag', wird: 'der Vertrag wird angelegt', fertig: 'Der Vertrag ist abgeschlossen.' },
  neue_laufzeit: { der: 'Der Vertrag', den: 'den Vertrag', wird: 'der Vertrag wird angelegt', fertig: 'Der Vertrag mit neuer Laufzeit ist abgeschlossen.' },
  bestandsvertrag: { der: 'Die Vertragsbestätigung', den: 'die Vertragsbestätigung', wird: 'die Bestätigung wird gespeichert', fertig: 'Der Vertrag ist digital erfasst.' },
  zusatzvereinbarung: { der: 'Die Zusatzvereinbarung', den: 'die Zusatzvereinbarung', wird: 'die Zusatzvereinbarung wird gespeichert', fertig: 'Die Zusatzvereinbarung gilt.' },
  zahlerwechsel: { der: 'Der Zahlerwechsel', den: 'den Zahlerwechsel', wird: 'das neue Mandat wird gespeichert', fertig: 'Der Zahlerwechsel ist abgeschlossen.' },
};

function restText(bis) {
  const s = Math.max(0, Math.round((new Date(bis).getTime() - Date.now()) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Inhalt ohne Rahmen — steht im eigenen Dialog (Verkaufsrechner) oder eingebettet in einem
    bestehenden Dialog (Bestandsvertrag, neue Laufzeit, Zusatzvereinbarung). */
/** qrZuerst (Upsell im Verkaufsrechner, 22.10.2026): der QR-Code ist der Hauptweg und steht groß oben,
    das iPad an der Theke zeigt den Vorgang trotzdem auch an. */
export function SendenInhalt({ vorgang, onSchliessen, onErneut, onFertig, sendet, eingebettet = false, qrZuerst = false }) {
  const [stand, setStand] = useState(vorgang);
  const [, tick] = useState(0);
  const fertigGemeldet = useRef(false);
  const fertigRef = useRef(onFertig); fertigRef.current = onFertig;

  useEffect(() => { setStand(vorgang); fertigGemeldet.current = false; }, [vorgang]);

  const abfragen = useCallback(async () => {
    try {
      const r = (await unterschriftAPI.status(vorgang.id)).data;
      setStand((s) => ({ ...s, ...r }));
      if (r.status === 'unterschrieben' && r.ergebnis && !fertigGemeldet.current) {
        fertigGemeldet.current = true;
        fertigRef.current(r.ergebnis);
      }
    } catch { /* Netz kurz weg: naechste Abfrage */ }
  }, [vorgang.id]);

  const laeuft = LAEUFT.includes(stand.status);
  useEffect(() => {
    if (!laeuft) return undefined;
    const t = setInterval(abfragen, ABFRAGE_MS);
    return () => clearInterval(t);
  }, [laeuft, abfragen]);

  // Verschwindet der Dialog (Seite gewechselt, Browser-Zurueck, Tab zu, Neuladen), waehrend der Vorgang noch
  // offen ist, wird er abgebrochen — sonst laege er bis zu 15 Minuten unbeobachtet am iPad, und eine
  // Unterschrift kaeme am iMac nie an. Laeuft der Abschluss schon, laesst der Server ihn unangetastet.
  const statusRef = useRef(stand.status); statusRef.current = stand.status;
  const lebt = useRef(false);
  useEffect(() => {
    lebt.current = true;
    const id = vorgang.id;
    const offen = () => ['offen', 'angezeigt'].includes(statusRef.current) && !fertigGemeldet.current;
    const beimVerlassen = () => { if (offen()) unterschriftAPI.abbrechenBeimVerlassen(id); };
    window.addEventListener('pagehide', beimVerlassen);
    return () => {
      window.removeEventListener('pagehide', beimVerlassen);
      lebt.current = false;
      // kurz warten: im Entwicklungsbetrieb (StrictMode) baut React sofort wieder auf — dann nicht abbrechen
      setTimeout(() => { if (!lebt.current && offen()) unterschriftAPI.abbrechen(id, 'Dialog am iMac geschlossen').catch(() => {}); }, 300);
    };
  }, [vorgang.id]);
  // Restzeit jede Sekunde; und dem Empfang melden, dass hier gearbeitet wird (Inaktivitaets-Ruecksprung ruht)
  useEffect(() => {
    if (!laeuft) return undefined;
    const t = setInterval(() => { tick((n) => n + 1); window.dispatchEvent(new Event('p1:aktiv')); }, 1000);
    return () => clearInterval(t);
  }, [laeuft]);

  // Schliessen bricht ab — ausser der Kunde war schneller: laeuft der Abschluss schon oder steht der
  // Vertrag bereits, bleibt der Dialog offen, bis das Ergebnis am iMac angekommen ist. Sonst ginge
  // der fertige Vertrag verloren und ein zweites Senden waere eine Umvertragung.
  const schliesstGerade = useRef(false);
  const abbrechen = async () => {
    if (stand.status === 'wird_abgeschlossen' || schliesstGerade.current) return;
    if (laeuft) {
      schliesstGerade.current = true;
      try {
        const r = (await unterschriftAPI.abbrechen(vorgang.id)).data;
        if (r.status === 'wird_abgeschlossen' || r.status === 'unterschrieben') {
          setStand((s) => ({ ...s, ...r }));
          if (r.status === 'unterschrieben' && r.ergebnis && !fertigGemeldet.current) {
            fertigGemeldet.current = true;
            fertigRef.current(r.ergebnis);
          }
          return;
        }
        statusRef.current = r.status || 'abgebrochen';      // erledigt — das Aufraeumen beim Abbau bricht nicht noch einmal ab
        setStand((x) => ({ ...x, ...r }));
      } catch { /* schon zu Ende oder Netz weg: schliessen, der Vorgang laeuft sonst von selbst ab */ }
      finally { schliesstGerade.current = false; }
    }
    onSchliessen();
  };
  const gesperrt = stand.status === 'wird_abgeschlossen';

  const stufe = stand.status === 'unterschrieben' ? 2 : (stand.status === 'angezeigt' || stand.status === 'wird_abgeschlossen') ? 1 : 0;
  const zuEnde = ['abgelaufen', 'abgebrochen'].includes(stand.status);
  const wo = stand.angezeigt?.weg === 'qr' ? 'auf dem Handy des Kunden' : 'auf dem iPad';
  const sache = SACHE[stand.art] || SACHE.vertrag;
  return (
      <div data-testid="unterschrift-inhalt">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><MonitorSmartphone className="w-5 h-5 text-marke" /> Zur Unterschrift gesendet</h3>
            <p className="text-sm text-muted-foreground mt-0.5">{stand.kunde} — {qrZuerst ? 'Code mit der Handy-Kamera scannen, lesen und unterschreiben.' : `${sache.der.charAt(0).toLowerCase() + sache.der.slice(1)} liegt am iPad an der Theke.`}</p>
          </div>
          {!eingebettet && <button type="button" onClick={abbrechen} disabled={gesperrt} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground disabled:opacity-40" aria-label="Schließen"><X className="w-4 h-4" /></button>}
        </div>

        {qrZuerst && laeuft && stand.qr && (
          <div className="mt-5 flex flex-col items-center gap-2 text-center" data-testid="unterschrift-qr-gross">
            <img src={stand.qr} alt="QR-Code zur Unterschrift am Handy" className="w-60 h-60 max-w-full rounded-lg border border-border bg-white" data-testid="unterschrift-qr" />
            <p className="text-xs text-muted-foreground max-w-sm">Gilt nur für diesen Vorgang, nur einmal und nur hier im Studio. Nicht weiterschicken. Steht das iPad an der Theke bereit, liegt es auch dort.</p>
          </div>
        )}

        <ol className="grid grid-cols-3 gap-2 mt-5" data-testid="unterschrift-schritte">
          {SCHRITTE.map(([key, label], i) => {
            const erreicht = !zuEnde && i <= stufe;
            const aktiv = !zuEnde && i === stufe && stand.status !== 'unterschrieben';
            return (
              <li key={key} className={`rounded-lg border px-3 py-2 text-sm ${erreicht ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : 'border-border/60 text-muted-foreground'}`}>
                <span className="flex items-center gap-1.5 font-medium">
                  {aktiv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : erreicht ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-current opacity-50" />}
                  {label}
                </span>
              </li>
            );
          })}
        </ol>

        <div className="mt-4 text-sm" data-testid="unterschrift-status" data-status={stand.status}>
          {stand.status === 'offen' && <p>{qrZuerst ? `Wartet darauf, dass der Code gescannt wird` : `Wartet darauf, dass das iPad ${sache.den} anzeigt`} … <span className="text-muted-foreground">(noch {restText(stand.laeuft_ab_am)} Minuten gültig)</span></p>}
          {stand.status === 'angezeigt' && <p>{sache.der} wird {wo} gelesen und unterschrieben. <span className="text-muted-foreground">(noch {restText(stand.laeuft_ab_am)} Minuten gültig)</span></p>}
          {stand.status === 'wird_abgeschlossen' && <p>Unterschrift erhalten — {sache.wird} …</p>}
          {stand.status === 'unterschrieben' && <p className="text-emerald-700 font-medium">Unterschrieben. {sache.fertig}</p>}
          {stand.status === 'abgelaufen' && <p className="text-amber-700 font-medium">Abgelaufen — nach 15 Minuten ohne Unterschrift. Bitte erneut senden.</p>}
          {stand.status === 'abgebrochen' && <p className="text-amber-700 font-medium">Abgebrochen{stand.grund ? `: ${stand.grund}` : ''}.</p>}
        </div>

        {!qrZuerst && laeuft && stand.qr && (
          <div className="mt-5 flex items-center gap-4 rounded-lg border border-border/60 p-3">
            <img src={stand.qr} alt="QR-Code zur Unterschrift am Handy" className="w-32 h-32 rounded-md border border-border bg-white" data-testid="unterschrift-qr" />
            <div className="text-sm">
              <p className="font-medium">Ausweichweg: am eigenen Handy unterschreiben</p>
              <p className="text-muted-foreground mt-1">Der Kunde scannt den Code hier am Bildschirm und sieht dieselbe Seite ohne Anmeldung. Der Code gilt nur für diesen Vorgang, nur einmal und nur an der Theke — nicht weiterschicken.</p>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          {zuEnde && <button type="button" onClick={onErneut} disabled={sendet} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-marke text-white hover:bg-marke-dunkel disabled:opacity-50" data-testid="unterschrift-erneut"><RefreshCw className={`w-4 h-4 ${sendet ? 'animate-spin' : ''}`} /> Erneut senden</button>}
          <button type="button" onClick={abbrechen} disabled={gesperrt} className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-accent disabled:opacity-50" data-testid="unterschrift-abbrechen">
            {laeuft && stand.status !== 'wird_abgeschlossen' ? 'Abbrechen' : (eingebettet ? 'Zurück' : 'Schließen')}
          </button>
        </div>
      </div>
  );
}

export default function SendenDialog(props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="unterschrift-dialog" role="dialog" aria-modal="true" aria-label="Zur Unterschrift gesendet">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-2xl w-full p-6">
        <SendenInhalt {...props} />
      </div>
    </div>
  );
}
